import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Check, 
  Upload, 
  FileText, 
  MapPin, 
  Calendar, 
  DollarSign, 
  Shield, 
  Target,
  Info,
  AlertTriangle,
  Plus,
  X,
  Star,
  Eye
} from 'lucide-react';
import api from '../../utils/api';

const CreateProject = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    description: '',
    projectType: '',
    location: {
      country: '',
      state: '',
      city: '',
      coordinates: { lat: '', lng: '' }
    },
    
    // Project Details
    methodology: '',
    projectSize: '',
    estimatedCredits: '',
    projectDuration: '',
    startDate: '',
    expectedCompletion: '',
    sustainabilityGoals: [],
    
    // Financial Information
    creditPrice: '',
    totalValue: '',
    currency: 'USD',
    paymentTerms: '',
    
    // Verification Options
    verificationType: '', // 'self-verified', 'unverified', 'request-verification', 'open-to-proposals'
    existingVerifications: [],
    verificationBudget: '',
    preferredStandards: [],
    verificationTimeline: '',
    verificationRequirements: '',
    
    // Self-Verified Details (Flow 1)
    thirdPartyVerifications: [],
    
    // Impact & Documentation
    environmentalImpact: {
      co2Reduction: '',
      biodiversityImpact: '',
      communityBenefits: '',
      waterConservation: '',
      soilImprovement: ''
    },
    documentation: {
      projectPlan: null,
      impactReports: [],
      certifications: [],
      financialDocuments: [],
      verificationDocuments: []
    },
    images: [],
    
    // Legal & Compliance
    legalCompliance: false,
    dataAccuracy: false,
    termsAccepted: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const projectTypes = [
    'Reforestation',
    'Renewable Energy',
    'Energy Efficiency',
    'Waste Management',
    'Agriculture & Land Use',
    'Transportation',
    'Industrial Process',
    'Carbon Capture',
    'Methane Reduction',
    'Blue Carbon'
  ];

  const verificationStandards = [
    'Gold Standard',
    'Verified Carbon Standard (VCS)',
    'Climate Action Reserve (CAR)',
    'Clean Development Mechanism (CDM)',
    'Plan Vivo',
    'American Carbon Registry (ACR)',
    'Climate Forward',
    'Social Carbon'
  ];

  const sustainabilityGoals = [
    'No Poverty',
    'Zero Hunger',
    'Good Health and Well-being',
    'Quality Education',
    'Gender Equality',
    'Clean Water and Sanitation',
    'Affordable and Clean Energy',
    'Decent Work and Economic Growth',
    'Industry, Innovation and Infrastructure',
    'Reduced Inequalities',
    'Sustainable Cities and Communities',
    'Responsible Consumption and Production',
    'Climate Action',
    'Life Below Water',
    'Life on Land',
    'Peace, Justice and Strong Institutions',
    'Partnerships for the Goals'
  ];

  const steps = [
    { number: 1, title: 'Basic Information', icon: Info },
    { number: 2, title: 'Project Details', icon: Target },
    { number: 3, title: 'Financial Information', icon: DollarSign },
    { number: 4, title: 'Verification Options', icon: Shield },
    { number: 5, title: 'Impact & Documentation', icon: FileText },
    { number: 6, title: 'Review & Submit', icon: Check }
  ];

  const handleInputChange = (field, value, nested = null) => {
    if (nested) {
      setFormData(prev => ({
        ...prev,
        [nested]: {
          ...prev[nested],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleArrayChange = (field, value, action = 'toggle') => {
    setFormData(prev => {
      const currentArray = prev[field] || [];
      let newArray;
      
      if (action === 'toggle') {
        newArray = currentArray.includes(value)
          ? currentArray.filter(item => item !== value)
          : [...currentArray, value];
      } else if (action === 'add') {
        newArray = [...currentArray, value];
      } else if (action === 'remove') {
        newArray = currentArray.filter((item, index) => index !== value);
      }
      
      return {
        ...prev,
        [field]: newArray
      };
    });
  };

  const addThirdPartyVerification = () => {
    const newVerification = {
      id: Date.now(),
      verificationBody: '',
      standard: '',
      registryId: '',
      verificationDate: '',
      expiryDate: '',
      scope: '',
      documents: []
    };
    
    setFormData(prev => ({
      ...prev,
      thirdPartyVerifications: [...prev.thirdPartyVerifications, newVerification]
    }));
  };

  const updateThirdPartyVerification = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      thirdPartyVerifications: prev.thirdPartyVerifications.map(verification =>
        verification.id === id ? { ...verification, [field]: value } : verification
      )
    }));
  };

  const removeThirdPartyVerification = (id) => {
    setFormData(prev => ({
      ...prev,
      thirdPartyVerifications: prev.thirdPartyVerifications.filter(v => v.id !== id)
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData.name.trim()) newErrors.name = 'Project name is required';
        if (!formData.description.trim()) newErrors.description = 'Project description is required';
        if (!formData.projectType) newErrors.projectType = 'Project type is required';
        if (!formData.location.country) newErrors.country = 'Country is required';
        break;
        
      case 2:
        if (!formData.methodology) newErrors.methodology = 'Methodology is required';
        if (!formData.projectSize) newErrors.projectSize = 'Project size is required';
        if (!formData.estimatedCredits) newErrors.estimatedCredits = 'Estimated credits is required';
        if (!formData.startDate) newErrors.startDate = 'Start date is required';
        break;
        
      case 3:
        if (!formData.creditPrice) newErrors.creditPrice = 'Credit price is required';
        if (!formData.paymentTerms) newErrors.paymentTerms = 'Payment terms are required';
        break;
        
      case 4:
        if (!formData.verificationType) newErrors.verificationType = 'Verification type is required';
        if (formData.verificationType === 'self-verified' && formData.thirdPartyVerifications.length === 0) {
          newErrors.thirdPartyVerifications = 'At least one third-party verification is required';
        }
        if (formData.verificationType === 'request-verification' && formData.preferredStandards.length === 0) {
          newErrors.preferredStandards = 'At least one preferred standard is required';
        }
        break;
        
      case 5:
        if (!formData.environmentalImpact.co2Reduction) {
          newErrors.co2Reduction = 'CO2 reduction estimate is required';
        }
        if (!formData.legalCompliance) newErrors.legalCompliance = 'Legal compliance confirmation is required';
        if (!formData.dataAccuracy) newErrors.dataAccuracy = 'Data accuracy confirmation is required';
        if (!formData.termsAccepted) newErrors.termsAccepted = 'Terms acceptance is required';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const calculateCredibilityScore = () => {
    let score = 50; // Base score
    
    if (formData.verificationType === 'self-verified') {
      const thirdPartyCount = formData.thirdPartyVerifications.length;
      if (thirdPartyCount >= 1) score += 20; // First third-party verification
      if (thirdPartyCount > 1) score += Math.min((thirdPartyCount - 1) * 10, 30); // Additional verifications
    }
    
    return Math.min(score, 100);
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;
    
    setIsSubmitting(true);
    
    try {
      // Calculate final project data
      const projectData = {
        name: formData.name,
        description: formData.description,
        projectType: formData.projectType,
        location: formData.location,
        methodology: formData.methodology,
        estimatedCredits: parseInt(formData.estimatedCredits),
        creditPrice: parseFloat(formData.creditPrice),
        totalValue: parseFloat(formData.totalValue),
        currency: formData.currency,
        paymentTerms: formData.paymentTerms,
        startDate: formData.startDate,
        sustainabilityGoals: formData.sustainabilityGoals,
        verificationType: formData.verificationType || 'request-verification',
        legalCompliance: formData.legalCompliance || true,
        dataAccuracy: formData.dataAccuracy || true,
        termsAccepted: formData.termsAccepted || true
      };
      
      console.log('Submitting project:', projectData);
      
      // Call real API
      const response = await api.projects.createProject(projectData);
      
      alert('Project submitted successfully! You will be notified once it\'s reviewed and approved.');
      
      // Reset form or redirect
      window.location.href = '/marketplace/my-projects';
      
    } catch (error) {
      console.error('Error submitting project:', error);
      alert('Error submitting project: ' + (error.message || 'Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderBasicInformation();
      case 2:
        return renderProjectDetails();
      case 3:
        return renderFinancialInformation();
      case 4:
        return renderVerificationOptions();
      case 5:
        return renderImpactDocumentation();
      case 6:
        return renderReviewSubmit();
      default:
        return null;
    }
  };

  const renderBasicInformation = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Project Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter your project name"
        />
        {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Project Description *
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          rows={4}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
            errors.description ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Describe your project's goals, methodology, and impact"
        />
        {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Project Type *
        </label>
        <select
          value={formData.projectType}
          onChange={(e) => handleInputChange('projectType', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
            errors.projectType ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value="">Select project type</option>
          {projectTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        {errors.projectType && <p className="text-red-500 text-sm mt-1">{errors.projectType}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Country *
          </label>
          <input
            type="text"
            value={formData.location.country}
            onChange={(e) => handleInputChange('country', e.target.value, 'location')}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
              errors.country ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Project country"
          />
          {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            State/Province
          </label>
          <input
            type="text"
            value={formData.location.state}
            onChange={(e) => handleInputChange('state', e.target.value, 'location')}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="State or province"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            City
          </label>
          <input
            type="text"
            value={formData.location.city}
            onChange={(e) => handleInputChange('city', e.target.value, 'location')}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="City"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Coordinates (Optional)
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={formData.location.coordinates.lat}
              onChange={(e) => handleInputChange('lat', e.target.value, 'location.coordinates')}
              className="w-1/2 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Latitude"
            />
            <input
              type="text"
              value={formData.location.coordinates.lng}
              onChange={(e) => handleInputChange('lng', e.target.value, 'location.coordinates')}
              className="w-1/2 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Longitude"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderProjectDetails = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Methodology *
        </label>
        <input
          type="text"
          value={formData.methodology}
          onChange={(e) => handleInputChange('methodology', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
            errors.methodology ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="e.g., CDM Methodology AM0001"
        />
        {errors.methodology && <p className="text-red-500 text-sm mt-1">{errors.methodology}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Project Size *
          </label>
          <input
            type="text"
            value={formData.projectSize}
            onChange={(e) => handleInputChange('projectSize', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
              errors.projectSize ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g., 1000 hectares, 50 MW"
          />
          {errors.projectSize && <p className="text-red-500 text-sm mt-1">{errors.projectSize}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Estimated Credits (tCO2e) *
          </label>
          <input
            type="number"
            value={formData.estimatedCredits}
            onChange={(e) => handleInputChange('estimatedCredits', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
              errors.estimatedCredits ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="100000"
          />
          {errors.estimatedCredits && <p className="text-red-500 text-sm mt-1">{errors.estimatedCredits}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Project Start Date *
          </label>
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
              errors.startDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Expected Completion
          </label>
          <input
            type="date"
            value={formData.expectedCompletion}
            onChange={(e) => handleInputChange('expectedCompletion', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Project Duration
        </label>
        <input
          type="text"
          value={formData.projectDuration}
          onChange={(e) => handleInputChange('projectDuration', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          placeholder="e.g., 10 years, 20 years"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          UN Sustainability Goals
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
          {sustainabilityGoals.map(goal => (
            <label key={goal} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.sustainabilityGoals.includes(goal)}
                onChange={() => handleArrayChange('sustainabilityGoals', goal)}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className="text-sm text-gray-700">{goal}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderFinancialInformation = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Credit Price per tCO2e *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-3 text-gray-500">$</span>
            <input
              type="number"
              step="0.01"
              value={formData.creditPrice}
              onChange={(e) => {
                handleInputChange('creditPrice', e.target.value);
                // Auto-calculate total value
                if (e.target.value && formData.estimatedCredits) {
                  const total = parseFloat(e.target.value) * parseFloat(formData.estimatedCredits);
                  handleInputChange('totalValue', total.toFixed(2));
                }
              }}
              className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.creditPrice ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="25.00"
            />
          </div>
          {errors.creditPrice && <p className="text-red-500 text-sm mt-1">{errors.creditPrice}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Total Project Value
          </label>
          <div className="relative">
            <span className="absolute left-3 top-3 text-gray-500">$</span>
            <input
              type="number"
              value={formData.totalValue}
              onChange={(e) => handleInputChange('totalValue', e.target.value)}
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-50"
              placeholder="2,500,000"
              readOnly
            />
          </div>
          <p className="text-sm text-gray-500 mt-1">Auto-calculated based on credit price</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Currency
        </label>
        <select
          value={formData.currency}
          onChange={(e) => handleInputChange('currency', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
        >
          <option value="USD">USD - US Dollar</option>
          <option value="EUR">EUR - Euro</option>
          <option value="GBP">GBP - British Pound</option>
          <option value="CAD">CAD - Canadian Dollar</option>
          <option value="AUD">AUD - Australian Dollar</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Payment Terms *
        </label>
        <select
          value={formData.paymentTerms}
          onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
            errors.paymentTerms ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value="">Select payment terms</option>
          <option value="immediate">Immediate Payment on Purchase</option>
          <option value="30-days">Net 30 Days</option>
          <option value="60-days">Net 60 Days</option>
          <option value="milestone">Milestone-based Payments</option>
          <option value="escrow">Escrow-based Payment</option>
        </select>
        {errors.paymentTerms && <p className="text-red-500 text-sm mt-1">{errors.paymentTerms}</p>}
      </div>

      <div className="bg-blue-50 p-4 rounded-lg">
        <h3 className="font-medium text-blue-900 mb-2">Financial Summary</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-blue-700">Estimated Credits:</span>
            <span className="font-medium ml-2">{formData.estimatedCredits || '0'} tCO2e</span>
          </div>
          <div>
            <span className="text-blue-700">Price per Credit:</span>
            <span className="font-medium ml-2">${formData.creditPrice || '0'}</span>
          </div>
          <div>
            <span className="text-blue-700">Total Value:</span>
            <span className="font-medium ml-2">${formData.totalValue || '0'}</span>
          </div>
          <div>
            <span className="text-blue-700">Platform Fee (15%):</span>
            <span className="font-medium ml-2">${formData.totalValue ? (parseFloat(formData.totalValue) * 0.15).toFixed(2) : '0'}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderVerificationOptions = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-yellow-800">Verification Options</h3>
            <p className="text-sm text-yellow-700 mt-1">
              Choose how you want to handle verification for your project. Different options provide different credibility levels.
            </p>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Verification Type *
        </label>
        <div className="space-y-4">
          {/* Flow 1: Self-Verified */}
          <div className={`p-4 border rounded-lg cursor-pointer transition-colors ${
            formData.verificationType === 'self-verified' 
              ? 'border-green-500 bg-green-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}>
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="verificationType"
                value="self-verified"
                checked={formData.verificationType === 'self-verified'}
                onChange={(e) => handleInputChange('verificationType', e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h3 className="font-medium text-gray-900">Third-Party Verified</h3>
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">High Credibility</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  I already have verification from accredited third-party verifiers
                </p>
              </div>
            </label>
          </div>

          {/* Flow 2: Unverified */}
          <div className={`p-4 border rounded-lg cursor-pointer transition-colors ${
            formData.verificationType === 'unverified' 
              ? 'border-green-500 bg-green-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}>
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="verificationType"
                value="unverified"
                checked={formData.verificationType === 'unverified'}
                onChange={(e) => handleInputChange('verificationType', e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h3 className="font-medium text-gray-900">Register Without Verification</h3>
                  <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">Base Credibility</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Register now, add verification later (can add third-party or request platform verification)
                </p>
              </div>
            </label>
          </div>

          {/* Flow 3: Request Verification */}
          <div className={`p-4 border rounded-lg cursor-pointer transition-colors ${
            formData.verificationType === 'request-verification' 
              ? 'border-green-500 bg-green-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}>
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="verificationType"
                value="request-verification"
                checked={formData.verificationType === 'request-verification'}
                onChange={(e) => handleInputChange('verificationType', e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h3 className="font-medium text-gray-900">Request Platform Verification</h3>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Platform Verified</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Request verification from platform verifiers (receive proposals from qualified verifiers)
                </p>
              </div>
            </label>
          </div>

          {/* Flow 4: Open to Proposals */}
          <div className={`p-4 border rounded-lg cursor-pointer transition-colors ${
            formData.verificationType === 'open-to-proposals' 
              ? 'border-green-500 bg-green-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}>
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="verificationType"
                value="open-to-proposals"
                checked={formData.verificationType === 'open-to-proposals'}
                onChange={(e) => handleInputChange('verificationType', e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h3 className="font-medium text-gray-900">Open to Verifier Proposals</h3>
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">Flexible</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Allow platform verifiers to proactively submit verification proposals
                </p>
              </div>
            </label>
          </div>
        </div>
        {errors.verificationType && <p className="text-red-500 text-sm mt-1">{errors.verificationType}</p>}
      </div>

      {/* Third-Party Verification Details (Flow 1) */}
      {formData.verificationType === 'self-verified' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Third-Party Verifications</h3>
            <button
              type="button"
              onClick={addThirdPartyVerification}
              className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Plus className="h-4 w-4" />
              <span>Add Verification</span>
            </button>
          </div>

          {formData.thirdPartyVerifications.map((verification, index) => (
            <div key={verification.id} className="p-4 border border-gray-300 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">Verification {index + 1}</h4>
                <button
                  type="button"
                  onClick={() => removeThirdPartyVerification(verification.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Body
                  </label>
                  <input
                    type="text"
                    value={verification.verificationBody}
                    onChange={(e) => updateThirdPartyVerification(verification.id, 'verificationBody', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., TÜV SÜD, DNV GL"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Standard
                  </label>
                  <select
                    value={verification.standard}
                    onChange={(e) => updateThirdPartyVerification(verification.id, 'standard', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Select standard</option>
                    {verificationStandards.map(standard => (
                      <option key={standard} value={standard}>{standard}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registry ID
                  </label>
                  <input
                    type="text"
                    value={verification.registryId}
                    onChange={(e) => updateThirdPartyVerification(verification.id, 'registryId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Registry identification number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Date
                  </label>
                  <input
                    type="date"
                    value={verification.verificationDate}
                    onChange={(e) => updateThirdPartyVerification(verification.id, 'verificationDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date
                  </label>
                  <input
                    type="date"
                    value={verification.expiryDate}
                    onChange={(e) => updateThirdPartyVerification(verification.id, 'expiryDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Scope
                  </label>
                  <input
                    type="text"
                    value={verification.scope}
                    onChange={(e) => updateThirdPartyVerification(verification.id, 'scope', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., Full project verification"
                  />
                </div>
              </div>
            </div>
          ))}

          {formData.thirdPartyVerifications.length === 0 && (
            <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center">
              <p className="text-gray-500 mb-2">No verifications added yet</p>
              <button
                type="button"
                onClick={addThirdPartyVerification}
                className="text-green-600 hover:text-green-800 font-medium"
              >
                Add your first verification
              </button>
            </div>
          )}

          {errors.thirdPartyVerifications && (
            <p className="text-red-500 text-sm">{errors.thirdPartyVerifications}</p>
          )}
        </div>
      )}

      {/* Platform Verification Request Details (Flow 3) */}
      {formData.verificationType === 'request-verification' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Verification Standards *
            </label>
            <div className="grid grid-cols-2 gap-2">
              {verificationStandards.map(standard => (
                <label key={standard} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.preferredStandards.includes(standard)}
                    onChange={() => handleArrayChange('preferredStandards', standard)}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">{standard}</span>
                </label>
              ))}
            </div>
            {errors.preferredStandards && <p className="text-red-500 text-sm mt-1">{errors.preferredStandards}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Budget Range
              </label>
              <select
                value={formData.verificationBudget}
                onChange={(e) => handleInputChange('verificationBudget', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select budget range</option>
                <option value="10000-25000">$10,000 - $25,000</option>
                <option value="25000-50000">$25,000 - $50,000</option>
                <option value="50000-100000">$50,000 - $100,000</option>
                <option value="100000-250000">$100,000 - $250,000</option>
                <option value="250000+">$250,000+</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Timeline
              </label>
              <select
                value={formData.verificationTimeline}
                onChange={(e) => handleInputChange('verificationTimeline', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select timeline</option>
                <option value="1-3months">1-3 months</option>
                <option value="3-6months">3-6 months</option>
                <option value="6-12months">6-12 months</option>
                <option value="12months+">12+ months</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verification Requirements & Scope
            </label>
            <textarea
              value={formData.verificationRequirements}
              onChange={(e) => handleInputChange('verificationRequirements', e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Describe specific verification requirements, methodologies needed, site visit requirements, etc."
            />
          </div>
        </div>
      )}

      {/* Credibility Score Preview */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-medium text-gray-900 mb-2">Project Credibility Score</h3>
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600">Current Score</span>
              <span className="font-medium">{calculateCredibilityScore()}/100</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${calculateCredibilityScore()}%` }}
              ></div>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Score based on verification type and completeness. Higher scores increase buyer confidence.
        </p>
      </div>
    </div>
  );

  const renderImpactDocumentation = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-medium text-gray-900 mb-4">Environmental Impact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CO2 Reduction (tCO2e/year) *
            </label>
            <input
              type="number"
              value={formData.environmentalImpact.co2Reduction}
              onChange={(e) => handleInputChange('co2Reduction', e.target.value, 'environmentalImpact')}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.co2Reduction ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="50000"
            />
            {errors.co2Reduction && <p className="text-red-500 text-sm mt-1">{errors.co2Reduction}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Biodiversity Impact
            </label>
            <input
              type="text"
              value={formData.environmentalImpact.biodiversityImpact}
              onChange={(e) => handleInputChange('biodiversityImpact', e.target.value, 'environmentalImpact')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="e.g., Habitat restoration for 50 species"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Community Benefits
            </label>
            <input
              type="text"
              value={formData.environmentalImpact.communityBenefits}
              onChange={(e) => handleInputChange('communityBenefits', e.target.value, 'environmentalImpact')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="e.g., 500 jobs created"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Water Conservation (liters/year)
            </label>
            <input
              type="number"
              value={formData.environmentalImpact.waterConservation}
              onChange={(e) => handleInputChange('waterConservation', e.target.value, 'environmentalImpact')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="1000000"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-medium text-gray-900 mb-4">Project Documentation</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Plan (PDF)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Upload project plan document</p>
              <input type="file" accept=".pdf" className="hidden" />
              <button 
                type="button"
                className="mt-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Choose File
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Images
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Upload project images (max 10 files)</p>
              <input type="file" accept="image/*" multiple className="hidden" />
              <button 
                type="button"
                className="mt-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Choose Files
              </button>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-medium text-gray-900 mb-4">Legal & Compliance</h3>
        <div className="space-y-4">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={formData.legalCompliance}
              onChange={(e) => handleInputChange('legalCompliance', e.target.checked)}
              className={`mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500 ${
                errors.legalCompliance ? 'border-red-500' : ''
              }`}
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Legal Compliance *</span>
              <p className="text-sm text-gray-600">
                I confirm that this project complies with all applicable local, national, and international laws and regulations.
              </p>
            </div>
          </label>
          {errors.legalCompliance && <p className="text-red-500 text-sm">{errors.legalCompliance}</p>}

          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={formData.dataAccuracy}
              onChange={(e) => handleInputChange('dataAccuracy', e.target.checked)}
              className={`mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500 ${
                errors.dataAccuracy ? 'border-red-500' : ''
              }`}
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Data Accuracy *</span>
              <p className="text-sm text-gray-600">
                I certify that all information provided is accurate and complete to the best of my knowledge.
              </p>
            </div>
          </label>
          {errors.dataAccuracy && <p className="text-red-500 text-sm">{errors.dataAccuracy}</p>}

          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={formData.termsAccepted}
              onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
              className={`mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500 ${
                errors.termsAccepted ? 'border-red-500' : ''
              }`}
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Terms & Conditions *</span>
              <p className="text-sm text-gray-600">
                I agree to the platform's terms and conditions, including verification requirements and commission structure.
              </p>
            </div>
          </label>
          {errors.termsAccepted && <p className="text-red-500 text-sm">{errors.termsAccepted}</p>}
        </div>
      </div>
    </div>
  );

  const renderReviewSubmit = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
        <h3 className="font-medium text-blue-900 mb-4">Project Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-blue-800">Project Name:</span>
            <p className="text-blue-700">{formData.name}</p>
          </div>
          <div>
            <span className="font-medium text-blue-800">Project Type:</span>
            <p className="text-blue-700">{formData.projectType}</p>
          </div>
          <div>
            <span className="font-medium text-blue-800">Location:</span>
            <p className="text-blue-700">{formData.location.city}, {formData.location.country}</p>
          </div>
          <div>
            <span className="font-medium text-blue-800">Estimated Credits:</span>
            <p className="text-blue-700">{formData.estimatedCredits} tCO2e</p>
          </div>
          <div>
            <span className="font-medium text-blue-800">Credit Price:</span>
            <p className="text-blue-700">${formData.creditPrice} per tCO2e</p>
          </div>
          <div>
            <span className="font-medium text-blue-800">Total Value:</span>
            <p className="text-blue-700">${formData.totalValue}</p>
          </div>
          <div>
            <span className="font-medium text-blue-800">Verification Type:</span>
            <p className="text-blue-700 capitalize">{formData.verificationType?.replace('-', ' ')}</p>
          </div>
          <div>
            <span className="font-medium text-blue-800">Credibility Score:</span>
            <p className="text-blue-700">{calculateCredibilityScore()}/100</p>
          </div>
        </div>
      </div>

      {formData.verificationType === 'self-verified' && formData.thirdPartyVerifications.length > 0 && (
        <div className="bg-green-50 p-6 rounded-lg border border-green-200">
          <h3 className="font-medium text-green-900 mb-4">Verification Summary</h3>
          <div className="space-y-2">
            {formData.thirdPartyVerifications.map((verification, index) => (
              <div key={verification.id} className="flex items-center space-x-3">
                <Check className="h-4 w-4 text-green-600" />
                <span className="text-sm text-green-800">
                  {verification.verificationBody} - {verification.standard}
                  {verification.verificationDate && ` (${verification.verificationDate})`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {formData.verificationType === 'request-verification' && (
        <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
          <h3 className="font-medium text-purple-900 mb-4">Verification Request</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium text-purple-800">Preferred Standards:</span>
              <p className="text-purple-700">{formData.preferredStandards.join(', ')}</p>
            </div>
            {formData.verificationBudget && (
              <div>
                <span className="font-medium text-purple-800">Budget Range:</span>
                <p className="text-purple-700">${formData.verificationBudget.replace('-', ' - ')}</p>
              </div>
            )}
            {formData.verificationTimeline && (
              <div>
                <span className="font-medium text-purple-800">Timeline:</span>
                <p className="text-purple-700">{formData.verificationTimeline.replace('-', ' - ')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-yellow-800">Next Steps</h3>
            <div className="text-sm text-yellow-700 mt-2 space-y-1">
              <p>1. Your project will be reviewed by our team (typically 2-3 business days)</p>
              <p>2. You'll receive an email notification once approved</p>
              {formData.verificationType === 'request-verification' && (
                <p>3. Platform verifiers will be notified and can submit proposals</p>
              )}
              {formData.verificationType === 'open-to-proposals' && (
                <p>3. Your project will be visible to verifiers for proactive proposals</p>
              )}
              <p>{formData.verificationType === 'request-verification' || formData.verificationType === 'open-to-proposals' ? '4' : '3'}. Once approved, your project will be listed in the marketplace</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Submitting...</span>
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              <span>Submit Project for Review</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Create New Project</h1>
          <p className="text-gray-600 mt-2">
            Register your carbon credit project with comprehensive verification options
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;
              
              return (
                <div key={step.number} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    isCompleted 
                      ? 'bg-green-500 border-green-500 text-white' 
                      : isActive 
                        ? 'border-green-500 text-green-500 bg-white' 
                        : 'border-gray-300 text-gray-400 bg-white'
                  }`}>
                    {isCompleted ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </div>
                  <div className="ml-3 hidden md:block">
                    <p className={`text-sm font-medium ${
                      isActive ? 'text-green-600' : isCompleted ? 'text-green-500' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`hidden md:block w-12 h-px mx-4 ${
                      isCompleted ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          {renderStepContent()}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Previous</span>
            </button>

            <div className="text-sm text-gray-500">
              Step {currentStep} of {steps.length}
            </div>

            {currentStep < steps.length ? (
              <button
                onClick={nextStep}
                className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <div></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateProject; 